import { Alert, Flex, Skeleton, Stack, Text, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';

import repeat from '~/common/utils/repeat';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { UserEntry } from '~/domains/contest/domains/services/interfaces/user-entry.interface';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import dt from '~/testing';

import { Section, SectionContent, SectionTitle } from '../components';
import EntryButton from '../../EntryButton/EntryButton';

type MyEntriesLayoutProps = {
  userEntries: PaginatedData<UserEntry>;
  fullContest: FullContest;
  isError: boolean;
  isLoading: boolean;
  children: ReactNode;
  handleAllViewClick: () => void;
  myEntriesStatus?: string;
};

const ITEMS_COUNT = 3;

function MyEntriesLayout({
  children,
  myEntriesStatus,
  isLoading,
  isError,
  userEntries,
  handleAllViewClick,
  fullContest,
}: MyEntriesLayoutProps) {
  const theme = useTheme();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });
  const { t } = useTranslation('contest');
  const isAllowedInLocation = fullContest?.is_allowed_in_location;
  const isSpectatorMode = fullContest?.public_viewing;

  return (
    <Section>
      <SectionTitle data-test-id={dt.contestDetail.section.myEntries.title}>
        {t('detail.entries.title', { status: myEntriesStatus })}
      </SectionTitle>
      <SectionContent>
        {isLoading && !isError && (
          <Stack spacing="lg">
            {repeat(ITEMS_COUNT, (index) => (
              <Skeleton
                h={isTablet ? 122 : 81}
                w="100%"
                key={index}
                data-test-id={dt.contestDetail.section.myEntries.skeleton}
              />
            ))}
          </Stack>
        )}
        {!isLoading && isError && <Alert color="red">{t('detail.entries.error')}</Alert>}
        {!isLoading && userEntries?.total === 0 && (
          <>
            <Alert color="gray" mb="md" data-test-id={dt.contestDetail.section.myEntries.noEntries}>
              {t('detail.entries.noData')}
            </Alert>
            <EntryButton
              contestId={fullContest?.id}
              entries={fullContest.entries}
              isAllowedInLocation={isAllowedInLocation}
              isEntertainmentOnly={fullContest.settings?.entertainmentOnlySettings?.enabled}
              isAfterDeadline={fullContest.is_after_first_late_swap_deadline}
              status={fullContest.status}
              isSpectatorMode={isSpectatorMode}
            />
          </>
        )}
        {!isLoading && userEntries?.total > 0 && (
          <Stack spacing={0}>
            {children}
            <Flex gap="md">
              {!isError && (
                <EntryButton
                  contestId={fullContest?.id}
                  entries={fullContest.entries}
                  isAllowedInLocation={isAllowedInLocation}
                  isEntertainmentOnly={fullContest.settings?.entertainmentOnlySettings?.enabled}
                  isAfterDeadline={fullContest.is_after_first_late_swap_deadline}
                  status={fullContest.status}
                  isSpectatorMode={isSpectatorMode}
                />
              )}
              {!isError && userEntries?.total > ITEMS_COUNT && (
                <UnstyledButton
                  onClick={handleAllViewClick}
                  data-test-id={dt.contestDetail.section.myEntries.viewAll}
                >
                  <Text color="cyan.5" size="sm">
                    {t('detail.entries.viewAll')}
                  </Text>
                </UnstyledButton>
              )}
            </Flex>
          </Stack>
        )}
      </SectionContent>
    </Section>
  );
}

export default MyEntriesLayout;
